import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import {
  BoxSection,
  ButtonDefault,
  ButtonPrimary,
  ConfirmModal,
  Heading3,
} from 'app/components';
import { StepContentButtonsArea } from '../styles';
import { ArrowBack } from '@mui/icons-material';
import { useCallback, useContext, useEffect, useState } from 'react';
import { ApplicationRequestContext } from '../ApplicationRequest';
import SearchInput from 'app/components/SearchFilter/SearchInput/SearchInput';
import EmptyState from 'app/components/EmptyState/EmptyState';
import { StyledSpinnerWrapper } from './ConsignedStep.styles';
import { useApplicationsService } from 'modules/products/services/hooks';
import { usePersonContext } from 'modules/customer/context';
import { serializePhoneObject } from 'app/utils/normalizer';
import {
  ConsignedAuthorizationTypes,
  ConsigneeStatus,
  ConsigneeOrganType,
  TypeProduct,
} from './interfaces';
import AuthorizationComponent, {
  IAuthorizationForm,
} from './Authorization/Authorization';
import AwaitingComponent from './Awaiting/Awaiting';
import AuthorizationSended from './AuthorizationSended/AuthorizationSended';
import { useSnackbar } from 'app/hooks/useSnackbar';
import AttentionCircularIcon from 'app/components/Icons/AttentionCircularIcon';
import SimulationComponent from './Simulation/Simulation';
import useCreditSimulatorV2 from 'app/hooks/useCreditSimulatorV2';
import { NewDownloadIcon } from 'app/components/Icons/DownloadIcon';
import { useTheme } from '@emotion/react';

const enum ConsignedSteps {
  EMPTY_STATE = 'EMPTY_STATE',
  SIMULATION = 'SIMULATION',
  LOADING = 'LOADING',
  AUTHORIZATION = 'AUTHORIZATION',
  SENDED = 'SENDED',
  ERROR = 'ERROR',
}

type IConsignedStepProps = {
  isCalcSimulation?: boolean;
};

const ConsignedStep = ({ isCalcSimulation }: IConsignedStepProps) => {
  // Hooks
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();
  const {
    loadings,
    getConsignedList,
    getConsigneeAuthorization,
    sendConsigneeTermAuthorization,
  } = useApplicationsService();
  const { downloadSimulation } = useCreditSimulatorV2();
  const { getPersonByTaxId, clearContext } = usePersonContext();
  const {
    onBack,
    onForward,
    consignedProduct,
    setConsignedList,
    consignedList,
    product,
    consignedError,
    setConsignedError,
  } = useContext(ApplicationRequestContext);

  // States
  const [isOpenOutOfAvailability, setIsOpenOutOfAvailability] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTaxId, setSelectedTaxId] = useState('');
  const [currentStep, setCurrentStep] = useState<ConsignedSteps>(
    ConsignedSteps.EMPTY_STATE,
  );

  // Variables
  const consignedId = consignedList?.id;
  const isLoanAuthorized: boolean =
    (consignedProduct?.authorization?.details?.filter(
      (item) => item.type_product === TypeProduct.LOAN,
    )[0].authorized &&
      !!consignedProduct?.available_balance) ||
    false;

  // Handlers
  const isOutOfAvailability = (): boolean => {
    const now = dayjs();
    const hour = now.hour();
    const minute = now.minute();

    // Entre 22:01 e 23:59
    const isLateNight = (hour === 22 && minute >= 1) || hour >= 23;

    // Entre 00:00 e 05:59
    const isEarlyMorning = hour < 6;

    return isLateNight || isEarlyMorning;
  };

  const getConsignedData = useCallback(
    async (taxId: string) => {
      if (isOutOfAvailability()) {
        setIsOpenOutOfAvailability(true);
      }

      setSelectedTaxId(taxId);
      clearContext();
      setConsignedList();

      if (product?.id) {
        if (product.organ_type === ConsigneeOrganType.CT) {
          setIsLoading(true);
          const response = await getConsigneeAuthorization(
            taxId,
            product?.id,
            setConsignedError,
          );

          if (response === '') {
            setIsLoading(false);
            return setCurrentStep(ConsignedSteps.EMPTY_STATE);
          }

          if (response === ConsigneeStatus.WAITING_SIGNATURE) {
            setIsLoading(false);
            return setCurrentStep(ConsignedSteps.SENDED);
          }

          if (response === ConsigneeStatus.NOT_AUTHORIZED) {
            setIsLoading(false);
            return setCurrentStep(ConsignedSteps.AUTHORIZATION);
          }

          setIsLoading(false);
        }

        const response = await getConsignedList(
          taxId,
          product?.id,
          setConsignedError,
        );

        if (response?.links) {
          await getPersonByTaxId(taxId);
          setConsignedList(response);

          return setCurrentStep(ConsignedSteps.SIMULATION);
        }
      }
      return setCurrentStep(ConsignedSteps.ERROR);
    },
    [product],
  );

  const sendTermAuthorization = useCallback(
    async (form: IAuthorizationForm) => {
      const response = await sendConsigneeTermAuthorization(
        {
          product_id: product?.id || '',
          name: form?.name || '',
          taxpayer_id: selectedTaxId,
          mode:
            (form?.type?.value as ConsignedAuthorizationTypes) ||
            ConsignedAuthorizationTypes.EMAIL,
          email: form?.email || '',
          phone: serializePhoneObject(form?.value || '') || undefined,
        },
        setConsignedError,
      );

      if (response) {
        return setCurrentStep(ConsignedSteps.SENDED);
      }

      showSnackbar('Não foi possível enviar a solicitação.');
      setCurrentStep(ConsignedSteps.AUTHORIZATION);
    },
    [selectedTaxId],
  );

  const downloadSimulationHandler = useCallback(async () => {
    if (consignedId) {
      const file = await downloadSimulation(consignedId);

      if (file) {
        saveAs(
          new Blob([file], {
            type: 'application/pdf',
          }),
          `simulacao_${consignedId}.pdf`,
        );
      }

      return;
    }

    showSnackbar(
      'Não foi possível fazer o download da consulta de margem. Atualize a página e tente novamente ou tente mais tarde',
      'error',
    );
  }, [consignedId]);

  const steps = {
    [ConsignedSteps.EMPTY_STATE]: (
      <EmptyState
        subtitle="Informe um CPF válido para a consulta de margem."
        title="A busca não encontrou nenhum resultado."
        hasMarginTop
      />
    ),
    [ConsignedSteps.ERROR]: (
      <EmptyState
        subtitle={
          consignedError ??
          'O CPF não está disponível para consulta de margem neste produto.'
        }
        title={
          consignedError
            ? 'Houve um problema na consulta.'
            : 'CPF não possui vínculos ativos.'
        }
        hasMarginTop
      />
    ),
    [ConsignedSteps.LOADING]: (
      <AwaitingComponent
        backStep={() => setCurrentStep(ConsignedSteps.EMPTY_STATE)}
        hideSubtitle={isLoading}
      />
    ),
    [ConsignedSteps.AUTHORIZATION]: (
      <AuthorizationComponent sendTermAuthorization={sendTermAuthorization} />
    ),
    [ConsignedSteps.SENDED]: (
      <AuthorizationSended
        backStep={() => setCurrentStep(ConsignedSteps.AUTHORIZATION)}
      />
    ),
    [ConsignedSteps.SIMULATION]: <SimulationComponent />,
  };

  useEffect(() => {
    if (loadings.isConsignedLoading || isLoading) {
      setCurrentStep(ConsignedSteps.LOADING);
    }

    if (!loadings.isConsignedLoading && consignedList) {
      setCurrentStep(ConsignedSteps.SIMULATION);
    }
  }, [loadings.isConsignedLoading, consignedList, isLoading]);

  return (
    <>
      <BoxSection>
        <Heading3>Consulta de margem</Heading3>
        <p style={{ marginBottom: '2rem' }}>
          O resultado da margem disponível pode variar até a efetivação da
          operação.
        </p>

        <SearchInput
          placeholder="Digite o CPF para consultar a margem"
          textButton="Consultar margem"
          searchHandler={getConsignedData}
        />

        <StyledSpinnerWrapper>{steps[currentStep]}</StyledSpinnerWrapper>

        <StepContentButtonsArea>
          {!isCalcSimulation && (
            <ButtonDefault margin={true} onClick={onBack}>
              <ArrowBack />
            </ButtonDefault>
          )}
          {consignedId && (
            <ButtonDefault onClick={downloadSimulationHandler}>
              <NewDownloadIcon
                color={theme?.palette?.brand.primary.base as string}
                size={22}
              />
            </ButtonDefault>
          )}
          <ButtonPrimary
            disabled={!isLoanAuthorized}
            onClick={() => {
              onForward();
            }}
            style={{ marginLeft: '1rem' }}
          >
            Avançar
          </ButtonPrimary>
        </StepContentButtonsArea>
      </BoxSection>

      {isOpenOutOfAvailability && (
        <ConfirmModal
          isOpen={isOpenOutOfAvailability}
          icon={<AttentionCircularIcon />}
          title="Fora do horário de funcionamento"
          btnConfirmText="Ok"
          handleConfirm={() => setIsOpenOutOfAvailability(false)}
          handleClose={() => setIsOpenOutOfAvailability(false)}
          isColumnButtons
        >
          <p>
            <b>Atenção: </b>
            De acordo com a disponibilidade da Dataprev, a operação não pode ser
            averbada entre 22h01 e 05h59. Recomendamos tentar novamente após
            esse horário.
          </p>
        </ConfirmModal>
      )}
    </>
  );
};

export default ConsignedStep;
